import type { WeekdayNumbers } from 'luxon';
import { DateTime, IANAZone } from 'luxon';

export const SINGLE_DAY_LUXON_DATE_FORMAT = 'MM-dd-yyyy';

export type ScheduledJobWithTimezone = {
	start: Date;
	end: Date;
	completedAt?: Date | null;
	startedAt?: Date | null;
	startTimezoned?: string;
	endTimezoned?: string;
};

export interface ITimestampedDBObj {
	updatedAt?: Date | null;
	createdAt: Date;
}

export class BaseDateHelper {
	public static getBusinessLocalDateTime(date: Date, timezone: string) {
		return DateTime.fromJSDate(date, { zone: timezone });
	}

	public static getBusinessLocalNow(timezone: string) {
		return BaseDateHelper.getBusinessLocalDateTime(new Date(), timezone);
	}

	public static getUTCDateFromDateTime(date: Date) {
		return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
	}

	/// Only use when the date cannot be created in the business timezone
	public static getEnvironmentLocalDateFromBusinessLocal(date: Date, timezone: string): Date;
	public static getEnvironmentLocalDateFromBusinessLocal(date: Date | null, timezone: string): Date | null;
	public static getEnvironmentLocalDateFromBusinessLocal(date: Date | null, timezone: string) {
		if (!date) return null;
		const businessLocalDate = BaseDateHelper.getBusinessLocalDateTime(date, timezone);
		return DateTime.local(
			businessLocalDate.year,
			businessLocalDate.month,
			businessLocalDate.day,
			businessLocalDate.hour,
			businessLocalDate.minute,
			businessLocalDate.second
		).toJSDate();
	}

	public static getEnvironmentLocalDateFromBusinessLocalWithLockedTime(date: Date, timezone: string): Date;
	public static getEnvironmentLocalDateFromBusinessLocalWithLockedTime(date: Date | null, timezone: string): Date | null;
	public static getEnvironmentLocalDateFromBusinessLocalWithLockedTime(date: Date | null, timezone: string) {
		if (!date) return null;
		const businessLocalDate = BaseDateHelper.getBusinessLocalDateTime(date, timezone);

		const localTimezone = DateTime.local().zoneName;
		return businessLocalDate.setZone(localTimezone, { keepLocalTime: true }).toJSDate();
	}

	/// The business's current calendar day, carried in a Date's *environment-local* fields. Date pickers compare
	/// calendar days in the environment zone, so a bound built from the business's end-of-day instant lands on the
	/// viewer's neighboring day. `now` is injectable so the conversion is testable without freezing the clock.
	public static getEnvironmentLocalBusinessToday(timezone: string, now = new Date()) {
		return BaseDateHelper.getEnvironmentLocalDateFromBusinessLocalWithLockedTime(now, timezone);
	}

	public static parseSingleDate(dateString: string, timezone: string) {
		return DateTime.fromFormat(dateString, SINGLE_DAY_LUXON_DATE_FORMAT, { zone: timezone });
	}

	public static getBusinessLocalDateFromEnvironmentLocal(date: Date, timezone: string): Date;
	public static getBusinessLocalDateFromEnvironmentLocal(date: Date | null, timezone: string): Date | null;
	public static getBusinessLocalDateFromEnvironmentLocal(date: Date | null, timezone: string) {
		if (!date) return null;
		const environmentLocalDate = DateTime.fromJSDate(date);
		return DateTime.fromObject(environmentLocalDate.toObject(), { zone: timezone }).toJSDate();
	}

	public static getBusinessLocalDateFromEnvironmentLocalWithLockedTime(date: Date, timezone: string): Date;
	public static getBusinessLocalDateFromEnvironmentLocalWithLockedTime(date: Date | null, timezone: string): Date | null;
	public static getBusinessLocalDateFromEnvironmentLocalWithLockedTime(date: Date | null, timezone: string) {
		if (!date) return null;
		const environmentLocalDate = DateTime.fromJSDate(date);
		return environmentLocalDate.setZone(timezone, { keepLocalTime: true }).toJSDate();
	}

	public static localizeUTC(utcDate: Date, timezone: string) {
		return DateTime.fromJSDate(utcDate, { zone: timezone }).toJSDate();
	}

	public static localizeUTCSafe(utcDate: Date, timezone: string): Date;
	public static localizeUTCSafe(utcDate: Date | null, timezone: string): Date | null;
	public static localizeUTCSafe(utcDate: Date | null, timezone: string) {
		if (utcDate) {
			return this.localizeUTC(utcDate, timezone);
		}
		return utcDate;
	}

	public static roundToQuarterHour(date: Date) {
		const currentMinutes = date.getMinutes();
		const minutesToNextQuarterHour = 15 - (currentMinutes % 15);
		const minutesToLastQuarterHour = currentMinutes % 15;

		if (minutesToLastQuarterHour < minutesToNextQuarterHour) {
			date.setMinutes(currentMinutes - minutesToLastQuarterHour);
			return date;
		} else {
			date.setMinutes(currentMinutes + minutesToNextQuarterHour);
			return date;
		}
	}

	public static formatDate({
		date,
		year = true,
		monthType = 'long',
		utc,
	}: {
		date: string | Date;
		year?: boolean;
		monthType?: 'short' | 'long' | 'numeric';
		utc?: boolean;
	}) {
		const dateObj = typeof date == 'string' ? new Date(date) : date;
		if (!year) {
			return dateObj.toLocaleDateString('en-US', { month: monthType, day: 'numeric', timeZone: utc ? 'UTC' : undefined });
		} else {
			return dateObj.toLocaleDateString('en-US', { month: monthType, day: 'numeric', year: 'numeric', timeZone: utc ? 'UTC' : undefined });
		}
	}

	// A compact date range that drops repeated month/year between the two ends:
	//   same month → "May 1 – 31, 2026"
	//   same year  → "Apr 28 – May 3, 2026"
	//   otherwise  → "Dec 30, 2025 – Jan 2, 2026"
	// Accepts Date or (serialized) ISO string for either end. monthType matches formatDate.
	public static formatDateRange({
		start,
		end,
		monthType = 'short',
		utc,
	}: {
		start: string | Date;
		end: string | Date;
		monthType?: 'short' | 'long' | 'numeric';
		utc?: boolean;
	}) {
		const timeZone = utc ? 'UTC' : undefined;
		const startObj = typeof start == 'string' ? new Date(start) : start;
		const endObj = typeof end == 'string' ? new Date(end) : end;

		const startYear = startObj.toLocaleDateString('en-US', { year: 'numeric', timeZone });
		const endYear = endObj.toLocaleDateString('en-US', { year: 'numeric', timeZone });
		const startMonth = startObj.toLocaleDateString('en-US', { month: monthType, timeZone });
		const endMonth = endObj.toLocaleDateString('en-US', { month: monthType, timeZone });

		const endLabel = BaseDateHelper.formatDate({ date: endObj, monthType, utc });
		if (startYear === endYear && startMonth === endMonth) {
			// "May 1 – 31, 2026": start keeps only its day, end carries month already dropped.
			const startDay = startObj.toLocaleDateString('en-US', { day: 'numeric', timeZone });
			return `${startMonth} ${startDay} – ${endObj.toLocaleDateString('en-US', { day: 'numeric', timeZone })}, ${endYear}`;
		}

		const startLabel = BaseDateHelper.formatDate({ date: startObj, monthType, utc, year: startYear !== endYear });
		return `${startLabel} – ${endLabel}`;
	}

	// A compact month/year range that drops the repeated year between the two ends:
	//   same year → "Jan – Dec 2026"
	//   otherwise → "Dec 2025 – Jan 2026"
	// Accepts Date or (serialized) ISO string for either end. Used for spans that are only meaningful at
	// month granularity (e.g. a job's billing-period coverage), unlike formatDateRange's day-level output.
	public static formatMonthRange({ start, end, utc }: { start: string | Date; end: string | Date; utc?: boolean }) {
		const timeZone = utc ? 'UTC' : undefined;
		const startObj = typeof start == 'string' ? new Date(start) : start;
		const endObj = typeof end == 'string' ? new Date(end) : end;

		const startYear = startObj.toLocaleDateString('en-US', { year: 'numeric', timeZone });
		const endYear = endObj.toLocaleDateString('en-US', { year: 'numeric', timeZone });
		const startMonth = startObj.toLocaleDateString('en-US', { month: 'short', timeZone });
		const endLabel = endObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone });

		if (startYear === endYear) {
			// "Jan – Dec 2026": the start drops its (shared) year, the end carries it.
			return `${startMonth} – ${endLabel}`;
		}
		return `${startObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone })} – ${endLabel}`;
	}

	public static formatTime(date: string | Date, hour12 = true, roundQuarterHour = false) {
		const dateObj = new Date(date);

		if (roundQuarterHour) {
			return BaseDateHelper.roundToQuarterHour(dateObj).toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12 });
		} else {
			return dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12 });
		}
	}

	public static formatDateOrTimeIfToday(date: Date) {
		const dateObject = new Date(date);

		const isToday = BaseDateHelper.isDateToday(date);

		if (isToday) {
			return dateObject.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
		}
		return dateObject.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	public static getLocalTimeOfDayOnDate(date: Date, time: string) {
		const [hour, minute] = time.split(':');
		if (!hour || !minute) {
			return undefined;
		}
		return DateTime.fromJSDate(date)
			.set({ hour: parseInt(hour), minute: parseInt(minute) })
			.toJSDate();
	}

	public static getDaysInRange(isMultiDayJob: boolean, startDate: Date, endDate: Date, isOnlyOnWeekdays?: boolean) {
		const daysInRange: DateTime[] = [];
		const startDateTime = DateTime.fromJSDate(startDate);
		const endDateTime = DateTime.fromJSDate(endDate);

		if (isMultiDayJob) {
			let currentDate = startDateTime;
			while (currentDate <= endDateTime) {
				if (!isOnlyOnWeekdays) {
					daysInRange.push(currentDate);
				} else {
					// Docs say there should be .isWeekend(), but TS doesn't agree
					if (![6, 7].includes(currentDate.weekday)) daysInRange.push(currentDate);
				}

				currentDate = currentDate.plus({ day: 1 });
			}
		} else {
			daysInRange.push(startDateTime);
		}

		return daysInRange;
	}

	public static formatDBObjectDateTime(obj: ITimestampedDBObj, year = false, monthType: 'short' | 'long' | 'numeric' = 'long') {
		return obj.updatedAt
			? `${BaseDateHelper.formatDate({ date: obj.updatedAt, year, monthType })}, ${BaseDateHelper.formatTime(obj.updatedAt)}`
			: `${BaseDateHelper.formatDate({ date: obj.createdAt, year, monthType })}, ${BaseDateHelper.formatTime(obj.createdAt)}`;
	}

	public static translateDates<T extends ScheduledJobWithTimezone>(scheduledJob: T, timezone: string) {
		scheduledJob.start = BaseDateHelper.localizeUTC(scheduledJob.start, timezone);
		scheduledJob.end = BaseDateHelper.localizeUTC(scheduledJob.end, timezone);
		scheduledJob.completedAt = scheduledJob.completedAt && BaseDateHelper.localizeUTCSafe(scheduledJob.completedAt, timezone);
		scheduledJob.startedAt = scheduledJob.startedAt && BaseDateHelper.localizeUTCSafe(scheduledJob.startedAt, timezone);
		scheduledJob.startTimezoned = DateTime.fromJSDate(scheduledJob.start, { zone: timezone }).toString();
		scheduledJob.endTimezoned = DateTime.fromJSDate(scheduledJob.end, { zone: timezone }).toString();
	}

	public static isDateToday(_date: Date | string | null | undefined) {
		if (!_date) return false;

		const today = DateTime.now();
		const date = DateTime.fromJSDate(new Date(_date));
		const isToday = date.hasSame(today, 'day');

		return isToday;
	}

	/// NB: Different defaults than formatDate
	public static formatAsTodayOrDate(date: Date | string, year = false, monthType: 'short' | 'long' | 'numeric' = 'short') {
		const isToday = BaseDateHelper.isDateToday(date);
		return isToday ? 'Today' : BaseDateHelper.formatDate({ date, year, monthType });
	}

	public static getDifferenceBetweenDateAndNow(date: Date) {
		// The time for many of the values in the database is off by the UTC offset, so for now we need to adjust the time accordingly.
		const offsetDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);

		return Date.now() - offsetDate.getTime();
	}

	public static getTimeDifferenceShort(date?: Date) {
		if (!date) return undefined;

		const timeDifference = BaseDateHelper.getDifferenceBetweenDateAndNow(date);

		const seconds = Math.floor(timeDifference / 1000);
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);
		const days = Math.floor(hours / 24);
		const weeks = Math.floor(days / 7);
		const months = Math.floor(weeks / 4.35);
		const years = Math.floor(months / 12);

		let timeAgo = 'Just now';

		if (years > 0) {
			return (timeAgo = `${years}y ago`);
		} else if (months > 0) {
			return (timeAgo = `${months}m ago`);
		} else if (weeks > 0) {
			return (timeAgo = `${weeks}w ago`);
		} else if (days > 0) {
			return (timeAgo = `${days}d ago`);
		} else if (hours > 0) {
			return (timeAgo = `${hours}h ago`);
		} else if (minutes > 0) {
			return (timeAgo = `${minutes}m ago`);
		} else if (seconds > 15) {
			return (timeAgo = `${seconds}s ago`);
		} else {
			return timeAgo;
		}
	}

	public static convertUNIXTimestampToDate(unixTimestamp: number) {
		return new Date(unixTimestamp * 1000);
	}

	// Compares behavior, not IANA names: a device in Indianapolis reports America/Indiana/Indianapolis,
	// which has kept the same clock as America/New_York since 2006. Sampling across the year catches
	// zones that share a standard offset but differ on DST (America/Phoenix vs America/Denver).
	public static timezonesShareRules({ now = new Date(), otherTimezone, timezone }: { now?: Date; otherTimezone: string; timezone: string }) {
		if (!IANAZone.isValidZone(timezone) || !IANAZone.isValidZone(otherTimezone)) {
			return false;
		}
		if (timezone === otherTimezone) {
			return true;
		}
		const zone = IANAZone.create(timezone);
		const otherZone = IANAZone.create(otherTimezone);
		const start = DateTime.fromJSDate(now);
		return [0, 3, 6, 9].every((months) => {
			const sampleMillis = start.plus({ months }).toMillis();
			return zone.offset(sampleMillis) === otherZone.offset(sampleMillis);
		});
	}

	public static comparator(filterLocalDateAtMidnight: Date, cellValue: Date) {
		const formattedDate = DateTime.fromJSDate(cellValue).startOf('day').toJSDate().getTime();
		if (filterLocalDateAtMidnight.getTime() === formattedDate) {
			return 0;
		}
		if (cellValue < filterLocalDateAtMidnight) {
			return -1;
		}
		if (cellValue > filterLocalDateAtMidnight) {
			return 1;
		}
		return 0;
	}

	public static getISODate(date: Date) {
		return DateTime.fromJSDate(date).toISODate();
	}

	/// The inverse of `getISODate`: a bare `YYYY-MM-DD` back to that day at environment-local midnight. Not
	/// `new Date(day)`, which reads a date-only string as UTC and so lands on the day before west of it.
	public static getDateFromISODate(day: string) {
		return DateTime.fromISO(day).toJSDate();
	}

	public static getDayFilter(date: DateTime) {
		const startDate = date.startOf('day');
		const endDate = startDate.endOf('day');

		return { startDate, endDate };
	}

	public static getWeekFilter(date: DateTime, cutoffDay: WeekdayNumbers, isLastWeek = false) {
		let startDate: DateTime;
		let endDate: DateTime;

		if (date.weekday >= cutoffDay) {
			startDate = date.startOf('week').set({ weekday: cutoffDay });
		} else {
			startDate = date.startOf('week').minus({ week: 1 }).set({ weekday: cutoffDay });
		}

		endDate = startDate.plus({ week: 1 }).minus({ day: 1 });

		if (isLastWeek) {
			startDate = startDate.minus({ week: 1 });
			endDate = endDate.minus({ week: 1 });
		}

		return { startDate, endDate };
	}

	public static getBiweeklyFilter(date: DateTime, cutoffDay: WeekdayNumbers, isBiweeklyOffset = false, isLastBiweekly = false) {
		const epoch = DateTime.fromMillis(0).setZone(date.zone);
		const duration = date.diff(epoch, 'week');
		const weeksSinceEpoch = Math.floor(duration.weeks);

		const isEvenWeek = weeksSinceEpoch % 2 === 0;

		let startDate: DateTime;
		let endDate: DateTime;

		if (date.weekday >= cutoffDay) {
			startDate = date.startOf('week').set({ weekday: cutoffDay });
		} else {
			startDate = date.startOf('week').minus({ week: 1 }).set({ weekday: cutoffDay });
		}

		if ((isEvenWeek && isBiweeklyOffset) || (!isEvenWeek && !isBiweeklyOffset)) {
			startDate = startDate.minus({ week: 1 });
		}

		endDate = startDate.plus({ week: 2 }).minus({ day: 1 });

		if (isLastBiweekly) {
			startDate = startDate.minus({ week: 2 });
			endDate = endDate.minus({ week: 2 });
		}

		return { startDate, endDate };
	}

	public static getMonthFilter(date: DateTime, isLastMonth = false) {
		let startDate = date.startOf('month');
		let endDate = startDate.endOf('month');

		if (isLastMonth) {
			startDate = startDate.minus({ month: 1 });
			endDate = endDate.minus({ month: 1 });
		}

		return { startDate, endDate };
	}

	public static getStartHalfOfMonthFilter(date: DateTime) {
		const startDate = date.startOf('month');
		const endDate = startDate.set({ day: 15 });

		return { startDate, endDate };
	}

	public static getEndHalfOfMonthFilter(date: DateTime, isLastMonth = false) {
		let startDate = date.set({ day: 16 });
		let endDate = startDate.endOf('month');

		if (isLastMonth) {
			startDate = startDate.minus({ month: 1 });
			endDate = endDate.minus({ month: 1 });
		}

		return { startDate, endDate };
	}

	public static getHalfOfMonthFilter(date: DateTime, isPreviousHalfMonth = false) {
		const isInFirstHalfOfMonth = date.day <= 15;

		if (isPreviousHalfMonth) {
			if (isInFirstHalfOfMonth) {
				return BaseDateHelper.getEndHalfOfMonthFilter(date, true);
			} else {
				return BaseDateHelper.getStartHalfOfMonthFilter(date);
			}
		} else {
			if (isInFirstHalfOfMonth) {
				return BaseDateHelper.getStartHalfOfMonthFilter(date);
			} else {
				return BaseDateHelper.getEndHalfOfMonthFilter(date);
			}
		}
	}

	public static getMonthRange(startDate: Date, endDate: Date) {
		const months: { month: string; monthNumber: string }[] = [];
		let monthDate = DateTime.fromJSDate(startDate).set({ day: 1 });

		while (monthDate <= DateTime.fromJSDate(endDate)) {
			months.push({ month: monthDate.toFormat('MMM'), monthNumber: monthDate.toFormat('yyyy-MM') });
			monthDate = monthDate.plus({ month: 1 });
		}

		return months;
	}
}
